import {
  type Tag,
  type GetFamilyIfManyTagsParams,
  validateGetFamilyIfManyTagsParams,
  shouldApplyTagGrouping,
  performTagGrouping,
} from './utils';

// Re-export types for backward compatibility
export type { Tag, GetFamilyIfManyTagsParams } from './utils';

/**
 * Reduces a large set of tags by grouping individual product tags under their family/parent tags.
 *
 * When a tag subset contains more than 7 items, this function simplifies the display by:
 * 1. Finding parent tags for individual product tags
 * 2. Preserving existing family tags (tags with '-family' suffix, 'old-products', or top-level tags)
 * 3. Combining and deduplicating the results
 *
 * This helps maintain a manageable number of tags in UI components while preserving
 * the hierarchical relationships between products and their families.
 *
 * @param params - Configuration object containing tag collections
 * @param params.tagsSubset - The subset of tags to potentially reduce
 * @param params.allTags - The complete collection of all available tags
 * @returns The original tagsSubset if <= 7 items, otherwise a reduced set of family/parent tags
 *
 * @throws Error if input validation fails
 *
 * @example
 * ```typescript
 * const tagsSubset = [
 *   { id: 1, slug: 'mk3s-plus', parent: 10 },
 *   { id: 2, slug: 'mk3s', parent: 10 },
 *   { id: 3, slug: 'mk4', parent: 10 },
 *   { id: 4, slug: 'xl', parent: 11 },
 *   { id: 5, slug: 'mini-plus', parent: 12 },
 *   { id: 6, slug: 'sla-family', parent: null },
 *   { id: 7, slug: 'old-products', parent: null },
 *   { id: 8, slug: 'some-accessory', parent: 10 }
 * ];
 *
 * const allTags = [
 *   ...tagsSubset,
 *   { id: 10, slug: 'fdm-family', parent: null },
 *   { id: 11, slug: 'xl-family', parent: null },
 *   { id: 12, slug: 'mini-family', parent: null }
 * ];
 *
 * const result = getFamilyIfManyTags({ tagsSubset, allTags });
 * // Returns: [
 * //   { id: 10, slug: 'fdm-family', parent: null },    // Parent of mk3s-plus, mk3s, mk4, some-accessory
 * //   { id: 11, slug: 'xl-family', parent: null },     // Parent of xl
 * //   { id: 12, slug: 'mini-family', parent: null },   // Parent of mini-plus
 * //   { id: 6, slug: 'sla-family', parent: null },     // Already a family tag
 * //   { id: 7, slug: 'old-products', parent: null }    // Special family tag
 * // ]
 * ```
 *
 * @example
 * ```typescript
 * // With fewer than 8 tags, returns original subset
 * const smallSubset = [
 *   { id: 1, slug: 'mk3s-plus', parent: 10 },
 *   { id: 2, slug: 'mk3s', parent: 10 }
 * ];
 *
 * const result = getFamilyIfManyTags({ tagsSubset: smallSubset, allTags });
 * // Returns: smallSubset (unchanged)
 * ```
 */
export function getFamilyIfManyTags(params: GetFamilyIfManyTagsParams): Tag[] {
  validateGetFamilyIfManyTagsParams(params);

  const { tagsSubset } = params;

  if (!shouldApplyTagGrouping(tagsSubset.length)) {
    return tagsSubset;
  }

  return performTagGrouping(params);
}
